/**
 * API Service for Subscription Extension
 * Handles all communication with the Django backend API
 */

import { API_BASE_URL as BASE_API_URL } from '../../../services/api';
import { getCurrentUserFromStore } from './auth';
import { getDeviceId, getDeviceIdentity } from './deviceIdentity';

// Use the base API URL from main services and append /subscriptions
const API_BASE_URL = `${BASE_API_URL}/subscriptions`;

// Store reference for API calls
let storeInstance = null;

/**
 * Set store instance for API service
 */
export function setStoreForAPI(store) {
    storeInstance = store;
}

/**
 * Get authentication token from store using selector
 */
function getAuthToken() {
    const user = getCurrentUserFromStore(storeInstance);
    // Try multiple possible token field names
    return user?.token || user?.access_token || user?.accessToken || '';
}

/**
 * Base fetch wrapper with authentication
 */
async function apiFetch(url, options = {}) {
    // Yield to the event loop so Redux-Persist rehydration completes before reading the token.
    // Without this yield, the token may be empty if the store is read before REHYDRATE fires.
    await Promise.resolve();
    const token = getAuthToken();

    const defaultHeaders = {
        'Content-Type': 'application/json',
    };

    if (token) {
        defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config = {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers
        }
    };

    const response = await fetch(url, config);

    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({ error: 'Request failed' }));
        const err = new Error(errorBody.error || errorBody.detail || `HTTP ${response.status}`);
        err.status = response.status;
        err.data = errorBody;
        throw err;
    }

    // Handle empty responses (like DELETE 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
        return null;
    }

    // Check if response has content before parsing JSON
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

class SubscriptionAPI {
    constructor() {
        this.baseURL = API_BASE_URL;
    }

    // Helper method to get auth token
    getAuthToken() {
        return getAuthToken();
    }

    // Helper method for making API requests
    async makeRequest(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        return await apiFetch(url, options);
    }

    // ========== Subscriptions ==========
    
    /**
     * Get subscriptions for a user
     * @param {number} subscriberId - The subscriber's user ID
     * @param {Object|string} params - Filter and pagination parameters, or legacy extensionKey
     * @param {string} params.extension_key - Optional extension key to filter by
     * @param {number} params.extension_id - Optional extension ID to filter by
     * @param {string} params.status - Optional status to filter by
     * @param {number} params.page - Page number
     * @param {number} params.page_size - Items per page
     */
    async getSubscriptions(subscriberId, params = {}) {
        const queryParams = new URLSearchParams();
        queryParams.append('subscriber_id', subscriberId);
        
        // Handle legacy parameter (for backward compatibility)
        if (typeof params === 'string') {
            // Old signature: getSubscriptions(subscriberId, extensionKey)
            const extensionKey = params;
            if (extensionKey) {
                queryParams.append('extension_key', extensionKey);
            }
        } else if (params && typeof params === 'object') {
            // New signature: getSubscriptions(subscriberId, { extension_key, extension_id, status, page, page_size })
            if (params.extension_key) queryParams.append('extension_key', params.extension_key);
            if (params.extension_id) queryParams.append('extension_id', params.extension_id);
            if (params.status) queryParams.append('status', params.status);
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);
        }
        
        const query = queryParams.toString();
        return this.makeRequest(`/subscriptions/?${query}`);
    }

    /**
     * Create a new subscription
     */
    async createSubscription(data) {
        return this.makeRequest('/create/', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    /**
     * Cancel subscription
     */
    /**
     * Retry payment for incomplete subscription
     * @param {string} subscriptionId - Subscription ID
     */
    async retryPayment(subscriptionId) {
        return this.makeRequest(`/subscriptions/${subscriptionId}/retry_payment/`, {
            method: 'POST'
        });
    }

    // ========== License Tokens (Offline Support) ==========
    
    /**
     * Get license token for offline validation
     * @param {string} extensionKey - Extension identifier
     *
     * Includes the current device id so the server can issue a device-bound
     * license token (payload carries allowed_devices + seats).
     */
    async getLicenseToken(extensionKey) {
        const deviceId = await getDeviceId();
        const query = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : '';
        return this.makeRequest(`/license/${extensionKey}/${query}`);
    }

    // ========== Devices (Seat Enforcement) ==========

    /**
     * Register the current device against the user's subscription(s).
     * Resolves device metadata automatically; callers may pass extensionKey to
     * scope registration to a single extension's subscription.
     *
     * @param {{ extension_key?: string }} [opts]
     * @returns {Promise<Object>} 200 -> { device, seats_used, seats_total }
     * @throws err with err.status === 409 and err.data.error === 'seat_limit_reached'
     */
    async registerDevice(opts = {}) {
        const identity = await getDeviceIdentity();
        if (!identity) {
            // Web build / no bridge — nothing to register.
            return { skipped: true, reason: 'no_device_identity' };
        }
        const payload = {
            device_id: identity.device_id,
            name: identity.name,
            platform: identity.platform,
            app_version: identity.app_version,
        };
        if (opts.extension_key) {
            payload.extension_key = opts.extension_key;
        }
        return this.makeRequest('/devices/register/', {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    /**
     * List devices bound to a subscriber (optionally filtered by extension).
     * @param {{ subscriber_id?: number|string, extension_key?: string }} params
     */
    async listDevices(params = {}) {
        const queryParams = new URLSearchParams();
        if (params.subscriber_id) queryParams.append('subscriber_id', params.subscriber_id);
        if (params.extension_key) queryParams.append('extension_key', params.extension_key);
        const query = queryParams.toString() ? `?${queryParams}` : '';
        return this.makeRequest(`/devices/${query}`);
    }

    /**
     * De-register a device, freeing its seat.
     * @param {string|number} deviceId - The SubscriptionDevice record id
     */
    async deregisterDevice(deviceId) {
        return this.makeRequest(`/devices/${deviceId}/`, {
            method: 'DELETE'
        });
    }

    /**
     * Heartbeat for the current device. Updates last_seen and reports whether
     * this device still holds an active seat.
     * @returns {Promise<{active:boolean, reason?:string}>}
     */
    async heartbeat() {
        const deviceId = await getDeviceId();
        if (!deviceId) {
            return { active: true, reason: 'no_device_identity' };
        }
        return this.makeRequest('/devices/heartbeat/', {
            method: 'POST',
            body: JSON.stringify({ device_id: deviceId })
        });
    }

    /**
     * Validate license token with server
     * @param {string} licenseToken - JWT license token
     */
    async validateLicenseToken(licenseToken) {
        return this.makeRequest('/license/validate/', {
            method: 'POST',
            body: JSON.stringify({ license_token: licenseToken })
        });
    }

    async cancelSubscription(subscriptionId, cancelAtPeriodEnd = true) {
        return this.makeRequest(`/${subscriptionId}/cancel/`, {
            method: 'POST',
            body: JSON.stringify({ cancel_at_period_end: cancelAtPeriodEnd })
        });
    }

    /**
     * Pause subscription
     */
    async pauseSubscription(subscriptionId) {
        return this.makeRequest(`/${subscriptionId}/pause/`, {
            method: 'POST'
        });
    }

    /**
     * Resume subscription
     */
    async resumeSubscription(subscriptionId) {
        return this.makeRequest(`/${subscriptionId}/resume/`, {
            method: 'POST'
        });
    }

    // ========== Entitlements ==========
    
    /**
     * Check entitlements for a user and extension
     * @param {number} subscriberId - The subscriber's user ID
     * @param {number|string} extensionIdOrKey - The extension ID (number) or key (string)
     * @param {string} featureKey - The feature key to check
     * @param {number} quantity - The quantity to check
     */
    async checkEntitlements(subscriberId, extensionIdOrKey, featureKey = '', quantity = 1) {
        const params = new URLSearchParams({
            subscriber_id: subscriberId,
            feature_key: featureKey,
            quantity: quantity
        });

        // Backend now accepts both extension_id (number) and extension_key (string)
        if (typeof extensionIdOrKey === 'number') {
            params.append('extension_id', extensionIdOrKey);
        } else if (typeof extensionIdOrKey === 'string') {
            params.append('extension_key', extensionIdOrKey);
        } else {
            throw new Error('extensionIdOrKey must be a number (extension_id) or string (extension_key)');
        }

        // Include the current device so the server can enforce seat limits.
        const deviceId = await getDeviceId();
        if (deviceId) {
            params.append('device_id', deviceId);
        }

        return this.makeRequest(`/entitlements/check/?${params}`);
    }

    // ========== Usage ==========
    
    /**
     * Record usage (idempotent)
     */
    async recordUsage(data, idempotencyKey) {
        return this.makeRequest('/usage/', {
            method: 'POST',
            headers: {
                'Idempotency-Key': idempotencyKey
            },
            body: JSON.stringify(data)
        });
    }

    // ========== Plans ==========
    
    /**
     * Get all plans for an extension
     * 
     * @param {Object|string|number} params - Filter and pagination parameters, or legacy extensionKeyOrId
     * @param {string|number} params.extension_id - Optional extension ID to filter by
     * @param {string} params.extension_key - Optional extension key to filter by
     * @param {string} params.status - Optional status to filter by
     * @param {string} params.search - Optional search term
     * @param {number} params.page - Page number
     * @param {number} params.page_size - Items per page
     * @param {string} status - Legacy parameter: Optional status to filter by (when params is extensionKeyOrId)
     * @returns {Promise<Object>} The plans data
     */
    async getPlans(params = {}, status = '') {
        const queryParams = new URLSearchParams();
        
        // Handle legacy parameters (for backward compatibility)
        if (typeof params === 'string' || typeof params === 'number') {
            // Old signature: getPlans(extensionKeyOrId, status)
            const extensionKeyOrId = params;
            const statusParam = status || '';
            if (extensionKeyOrId) {
                if (typeof extensionKeyOrId === 'number') {
                    queryParams.append('extension_id', extensionKeyOrId);
                } else {
                    queryParams.append('extension_key', extensionKeyOrId);
                }
            }
            if (statusParam) queryParams.append('status', statusParam);
        } else if (params && typeof params === 'object') {
            // New signature: getPlans({ extension_id, extension_key, status, search, page, page_size })
            if (params.extension_id) queryParams.append('extension_id', params.extension_id);
            if (params.extension_key) queryParams.append('extension_key', params.extension_key);
            if (params.status) queryParams.append('status', params.status);
            if (params.search) queryParams.append('search', params.search);
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);
        }
        
        const query = queryParams.toString() ? `?${queryParams}` : '';
        return this.makeRequest(`/plans/${query}`);
    }

    /**
     * Get a specific plan
     */
    async getPlan(planId) {
        return this.makeRequest(`/plans/${planId}/`);
    }

    /**
     * Create a new plan
     */
    async createPlan(data) {
        return this.makeRequest('/plans/', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    /**
     * Update a plan
     */
    async updatePlan(planId, data) {
        return this.makeRequest(`/plans/${planId}/`, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    }

    /**
     * Archive a plan
     */
    async archivePlan(planId) {
        return this.updatePlan(planId, { status: 'archived' });
    }

    // ========== Pricing ==========
    
    /**
     * Get all prices for a plan or extension
     * @param {string} planId - Optional plan ID to filter by
     * @param {string|number} extensionIdOrKey - Optional extension ID or key to filter by
     */
    async getPrices(planId = '', extensionIdOrKey = '') {
        const params = new URLSearchParams();
        if (planId) params.append('plan_id', planId);
        if (extensionIdOrKey) {
            if (typeof extensionIdOrKey === 'number') {
                params.append('extension_id', extensionIdOrKey);
            } else {
                params.append('extension_key', extensionIdOrKey);
            }
        }
        const query = params.toString() ? `?${params}` : '';
        return this.makeRequest(`/prices/${query}`);
    }

    /**
     * Create a new price
     */
    async createPrice(data) {
        return this.makeRequest('/prices/', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    /**
     * Update a price
     */
    async updatePrice(priceId, data) {
        return this.makeRequest(`/prices/${priceId}/`, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    }

    /**
     * Delete a price
     */
    async deletePrice(priceId) {
        return this.makeRequest(`/prices/${priceId}/`, {
            method: 'DELETE'
        });
    }

    // ========== Features ==========
    
    /**
     * Get all features for an extension
     * @param {Object|string} params - Filter and pagination parameters, or legacy extensionKey
     * @param {number} params.extension_id - Optional extension ID to filter by
     * @param {string} params.extension_key - Optional extension key to filter by
     * @param {string} params.type - Optional feature type to filter by
     * @param {string} params.search - Optional search term
     * @param {number} params.page - Page number
     * @param {number} params.page_size - Items per page
     */
    async getFeatures(params = {}) {
        const queryParams = new URLSearchParams();
        
        // Handle legacy parameter (for backward compatibility)
        if (typeof params === 'string') {
            // Old signature: getFeatures(extensionKey)
            const extensionKey = params;
            if (extensionKey) {
                queryParams.append('extension_key', extensionKey);
            }
        } else if (params && typeof params === 'object') {
            // New signature: getFeatures({ extension_id, extension_key, type, search, page, page_size })
            if (params.extension_id) queryParams.append('extension_id', params.extension_id);
            if (params.extension_key) queryParams.append('extension_key', params.extension_key);
            if (params.type) queryParams.append('type', params.type);
            if (params.search) queryParams.append('search', params.search);
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);
        }
        
        const query = queryParams.toString() ? `?${queryParams}` : '';
        return this.makeRequest(`/features/${query}`);
    }

    /**
     * Create a new feature
     */
    async createFeature(data) {
        return this.makeRequest('/features/', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    // ========== Extensions ==========
    
    /**
     * Get all extensions with subscription statistics
     * @param {Object} params - Pagination and filter parameters
     * @param {number} params.page - Page number (default: 1)
     * @param {number} params.page_size - Items per page (default: 50)
     * @param {string} params.search - Search term
     */
    async getExtensions(params = {}) {
        try {
            // Fetch both extension data and subscription stats in parallel
            const [extensionsResponse, statsResponse] = await Promise.all([
                this.getExtensionsFromMainAPI(params),
                this.makeRequest('/analytics/extensions-stats/')
            ]);
            
            // Handle paginated extension response
            const extensions = extensionsResponse.results || extensionsResponse || [];
            const stats = statsResponse.extensions || [];
            
            // Create a map of extension stats by ID
            const statsMap = {};
            stats.forEach(stat => {
                statsMap[stat.id] = stat;
            });
            
            // Merge extension data with stats
            const enrichedExtensions = extensions.map(ext => {
                const extStats = statsMap[ext.id] || {};
                return {
                    ...ext,
                    plans_count: extStats.plans_count || 0,
                    features_count: extStats.features_count || 0,
                    meters_count: extStats.meters_count || 0,
                    subscriptions_count: extStats.subscriptions_count || 0,
                    active_subscriptions_count: extStats.active_subscriptions_count || 0
                };
            });
            
            // Return enriched extensions with pagination info
            return {
                results: enrichedExtensions,
                count: extensionsResponse.count || enrichedExtensions.length,
                next: extensionsResponse.next,
                previous: extensionsResponse.previous
            };
        } catch (error) {
            console.error('Error fetching extensions:', error);
            throw error;
        }
    }
    
    /**
     * Get extensions from main extensions API (internal helper)
     * @param {Object} params - Pagination and filter parameters
     */
    async getExtensionsFromMainAPI(params = {}) {
        // Use the base API URL (without /subscriptions)
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page);
        if (params.page_size) queryParams.append('page_size', params.page_size);
        if (params.search) queryParams.append('search', params.search);
        
        const query = queryParams.toString() ? `?${queryParams}` : '';
        const url = `${BASE_API_URL}/extensions/extensions/${query}`;
        
        const token = getAuthToken();
        const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        
        if (!response.ok) {
            throw new Error(`Failed to fetch extensions: ${response.statusText}`);
        }
        
        const data = await response.json();
        // Return paginated response structure
        return {
            results: data.results || data || [],
            count: data.count || (Array.isArray(data) ? data.length : 0),
            next: data.next,
            previous: data.previous
        };
    }

    /**
     * Get a specific extension (from main extensions API)
     */
    async getExtension(extensionKey) {
        // Use the base API URL (without /subscriptions)
        const url = `${BASE_API_URL}/extensions/extensions/${extensionKey}/`;
        
        const token = getAuthToken();
        const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });
        
        if (!response.ok) {
            throw new Error(`Failed to fetch extension: ${response.statusText}`);
        }
        
        return response.json();
    }

    // ========== Additional Plan Methods ==========

    /**
     * Get plans with pagination and filtering
     */
    async getPlansWithFilters({ extension_id, status, search, page, page_size, ordering }) {
        const params = new URLSearchParams();
        if (extension_id) params.append('extension_id', extension_id);
        if (status) params.append('status', status);
        if (search) params.append('search', search);
        if (page) params.append('page', page);
        if (page_size) params.append('page_size', page_size);
        if (ordering) params.append('ordering', ordering);
        const query = params.toString() ? `?${params}` : '';
        return this.makeRequest(`/plans/${query}`);
    }

    // ========== Meters ==========

    /**
     * Get all meters for an extension
     */
    async getMeters(extensionId = '') {
        const query = extensionId ? `?extension_id=${extensionId}` : '';
        return this.makeRequest(`/meters/${query}`);
    }

    /**
     * Create a new meter
     */
    async createMeter(data) {
        return this.makeRequest('/meters/', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    /**
     * Update a meter
     */
    async updateMeter(meterId, data) {
        return this.makeRequest(`/meters/${meterId}/`, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    }

    /**
     * Delete a meter
     */
    async deleteMeter(meterId) {
        return this.makeRequest(`/meters/${meterId}/`, {
            method: 'DELETE'
        });
    }

    // ========== Subscription Lifecycle ==========

    /**
     * Update subscription (general update)
     */
    async updateSubscription(subscriptionId, data) {
        return this.makeRequest(`/${subscriptionId}/`, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    }

    /**
     * Upgrade subscription to a new plan
     */
    async upgradeSubscription(subscriptionId, { plan_id, price_id, proration_behavior = 'create_prorations' }) {
        return this.makeRequest(`/${subscriptionId}/upgrade/`, {
            method: 'POST',
            body: JSON.stringify({ plan_id, price_id, proration_behavior })
        });
    }

    /**
     * Apply feature overrides to subscription
     */
    async applyOverride(subscriptionId, features) {
        return this.makeRequest(`/${subscriptionId}/override/`, {
            method: 'POST',
            body: JSON.stringify({ features })
        });
    }

    // ========== Usage & Analytics ==========

    /**
     * Get usage records with filtering
     */
    async getUsage({ subscriber_id, extension_id, meter_key, from, to, page, page_size }) {
        const params = new URLSearchParams();
        if (subscriber_id) params.append('subscriber_id', subscriber_id);
        if (extension_id) params.append('extension_id', extension_id);
        if (meter_key) params.append('meter_key', meter_key);
        if (from) params.append('from', from);
        if (to) params.append('to', to);
        if (page) params.append('page', page);
        if (page_size) params.append('page_size', page_size);
        const query = params.toString() ? `?${params}` : '';
        return this.makeRequest(`/usage/${query}`);
    }

    /**
     * Export usage to CSV
     */
    async exportUsage(params) {
        const queryParams = new URLSearchParams(params);
        const url = `${this.baseURL}/usage/export.csv?${queryParams}`;
        const token = this.getAuthToken();
        
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        
        if (!response.ok) {
            throw new Error(`Export failed: ${response.status}`);
        }
        
        return response.blob();
    }

    /**
     * Get usage statistics
     */
    async getUsageStats({ extension_id, meter_id, start_date, end_date } = {}) {
        const params = new URLSearchParams();
        if (extension_id) params.append('extension_id', extension_id);
        if (meter_id) params.append('meter_id', meter_id);
        if (start_date) params.append('start_date', start_date);
        if (end_date) params.append('end_date', end_date);
        const query = params.toString() ? `?${params}` : '';
        return this.makeRequest(`/analytics/usage-stats/${query}`);
    }

    // ========== Reports and Analytics ==========

    /**
     * Get dashboard statistics (active subs, MRR, trials, usage, recent activity)
     */
    async getDashboardStats() {
        return this.makeRequest('/dashboard/stats/');
    }

    /**
     * Get MRR (Monthly Recurring Revenue) report
     */
    async getMRRReport({ extension_id } = {}) {
        const params = new URLSearchParams();
        if (extension_id) params.append('extension_id', extension_id);
        const query = params.toString() ? `?${params}` : '';
        return this.makeRequest(`/reports/mrr/${query}`);
    }

    /**
     * Get trials ending soon
     */
    async getTrialsEnding(days = 7) {
        return this.makeRequest(`/reports/trials-ending/?days=${days}`);
    }

    // ========== Feature Management ==========

    /**
     * Delete a feature
     */
    async deleteFeature(featureId) {
        return this.makeRequest(`/features/${featureId}/`, {
            method: 'DELETE'
        });
    }

    /**
     * Update a feature
     */
    async updateFeature(featureId, data) {
        return this.makeRequest(`/features/${featureId}/`, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    }
}

// Export both the class and a singleton instance
export { SubscriptionAPI };
export default new SubscriptionAPI();

